<template>
  <router-link class="highlights-card-parts" :to="card.link">
    <div class="highlights-card-parts__header">
      <p class="highlights-card-parts__situation">
        <span class="highlights-card-parts__situation__lap">
          Lap<span v-text="card.lap" />-{{ card.sector }}
        </span>
        <span class="highlights-card-parts__situation__round">/ {{ card.round }}</span>
      </p>
      <p class="highlights-card-parts__situation__lapTime">{{ card.movieStartTime }}</p>
    </div>
    <figure class="highlights-card-parts__scene-thumbnail">
      <img class="highlights-card-parts__scene-image" :src="card.thumbnailPath" alt="" />
    </figure>
    <div class="highlights-card-parts__body">
      <figure class="highlights-card-parts__user-image">
        <img :src="card.createUser.userPicture" alt="" />
      </figure>
      <p class="highlights-card-parts__user-name" v-text="card.createUser.userDisplayName" />
      <p class="highlights-card-parts__comment" v-html="card.note" />
    </div>
  </router-link>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from '@vue/composition-api'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import type { UserRetrieveNameResponseType } from '@/store/stores/collectionModule/documents/user/UserRetrieveNameDocument'
import RaceDocument from '@/store/stores/collectionModule/documents/race/RaceDocument'
import ChampionshipDocument from '@/store/stores/collectionModule/documents/championship/ChampionshipDocument'
import I18n from '@/locales/I18n'

/* eslint-disable @typescript-eslint/no-var-requires */
const defaultIcon = require('@/assets/img/icon/icon_user_default.png')

/**
 * 新着ハイライト作成ユーザーのデータ型
 */
type CreateHighlightUserType = {
  userDisplayName: string
  userPicture: string
}

/**
 * 新着ハイライトカードのデータ型
 */
type HomeHighlightCardType = {
  highlightId: string
  lap: string
  sector: string
  round: string
  movieStartTime: string
  note: string
  thumbnailPath: string
  link: string
  createUser: CreateHighlightUserType
}

export default defineComponent({
  name: 'HighlightsCardParts',
  props: {
    /**
     * 大会データ
     */
    championships: {
      type: Array as PropType<Array<ChampionshipDocument>>,
      required: true,
    },
    /**
     * レースデータ
     */
    raceList: {
      type: Array as PropType<Array<RaceDocument>>,
      required: true,
    },
    /**
     * ハイライトデータ
     */
    highlight: {
      type: Object as PropType<HighlightDocument>,
      required: true,
    },
    /**
     * 新着ハイライトで利用するユーザー情報
     */
    retrieveNameUsersByUserId: {
      type: Object as PropType<Record<string, UserRetrieveNameResponseType>>,
      required: true,
    },
  },
  setup(props) {
    /**
     * 新着ハイライトカードのデータ
     */
    const card = computed((): HomeHighlightCardType => {
      const propsHighlight = props.highlight
      const propsUsersByUserId = props.retrieveNameUsersByUserId

      // 対象のレース
      const targetRace = props.raceList.find((race) => race.id === propsHighlight.matchId)
      // 対象の大会
      const targetChampionship = props.championships.find(
        (championship) => championship.id === targetRace?.championshipMasterId,
      )
      const createdUser = propsUsersByUserId?.[propsHighlight._createdBy ?? '']
        ? propsUsersByUserId?.[propsHighlight._createdBy ?? '']
        : undefined

      return {
        highlightId: propsHighlight.highlightId ?? '',
        lap: propsHighlight.additionalData?.lap || '-',
        sector: propsHighlight?.additionalData?.sector ?? '--',
        round: targetChampionship?.round
          ? `Rd.${targetChampionship?.round}`
          : targetChampionship?.circuitName[I18n.locale] ?? '',
        movieStartTime: propsHighlight.raceTime,
        note: propsHighlight.note ?? '',
        thumbnailPath: propsHighlight.thumbnailUri
          ? CloudFrontUtil.getSignedUrl(propsHighlight.thumbnailUri)
          : '',
        createUser: {
          userDisplayName: createdUser?.additionalData?.userDisplayName ?? '',
          userPicture: createdUser?.userPicture
            ? CloudFrontUtil.getSignedUrl(createdUser?.userPicture)
            : defaultIcon,
        },
        link: targetRace
          ? `/race-video/${targetRace.championshipMasterId}/${targetRace.id}/${propsHighlight?.highlightId}/?playType=highlight&highlightId=${propsHighlight?.highlightId}`
          : '',
      }
    })

    return {
      card,
    }
  },
})
</script>

<style scoped lang="scss">
.highlights-card-parts {
  flex-shrink: 0;
  width: 185px;
  min-height: 222px;
  color: $white;
  text-align: left;
  background-color: $black20;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 rgba(#000, 0.1);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;
    padding: 0 8px;
  }

  &__lock {
    position: absolute;
    right: 6px;
    bottom: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    color: $white;
    background-color: rgba(#000, 0.15);
    border-radius: 50%;

    &-icon {
      width: 14px;
      height: 14px;
    }
  }

  &__situation {
    font-size: 11px;
    font-family: $fontSignup;

    &__lap {
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.04em;

      &Time {
        padding: 0 4px;
        font-size: 10px;
        font-weight: 500;
        letter-spacing: 0.04em;
        background-color: $black70;
        border-radius: 2px;
      }
    }

    &__round {
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.04em;
      display: inline-block;
      width: 50px;
      vertical-align: top;
      line-height: 16px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  &__scene {
    &-thumbnail {
      position: relative;
    }

    &-image {
      width: 100%;
      height: 63px;
      object-fit: cover;
    }
  }

  &__body {
    position: relative;
    padding: 0 8px 10px;
  }

  &__user {
    &-image {
      margin-top: -24px;
      margin-bottom: 6px;

      img {
        width: 50px;
        height: 50px;
        overflow: hidden;
        border-radius: 50%;
        background: $white;
        object-fit: cover;
      }
    }

    &-name {
      width: 100%;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.02em;
      font-feature-settings: 'palt' on;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  &__comment {
    width: 100%;
    height: 63px; // 11px * 1.45(line-height) * 4(行数) = 63.8px
    margin-top: 2px;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 11px;
    line-height: 1.45;
    letter-spacing: 0.04em;
    font-feature-settings: 'palt' on;
    display: -webkit-box;
    overflow: hidden;

    /* autoprefixer: off */
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
  }

  /* autoprefixer: on */
}
</style>
